// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { meetingAPI, categoryAPI, courseAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Plus, 
  Video, 
  Trash2,
  ExternalLink,
  Loader2,
  Calendar as CalendarIcon,
  Clock,
  Users,
  Pencil,
  Power,
  PowerOff,
  Infinity,
  Monitor
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime, isDateFuture, getDaysUntil, cn } from "@/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CategoryManagerButton } from "@/components/CategoryManagerButton";
import { CourseGrouping } from "@/components/CourseGrouping";
import LiveAttendanceDashboard from "@/components/presence/LiveAttendanceDashboard";

export default function MeetingsPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    room_name: "",
    scheduled_at: new Date(),
    scheduled_time: "10:00",
    duration_minutes: 60,
    meeting_type: "jitsi",
    external_link: "",
    is_always_active: false,
    category_id: "",
  });
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    room_name: "",
    scheduled_at: new Date(),
    scheduled_time: "10:00",
    duration_minutes: 60,
    meeting_type: "jitsi",
    external_link: "",
    is_always_active: false,
    category_id: "",
  });
  const [togglingId, setTogglingId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    loadMeetings();
    categoryAPI.list(workspaceId).then(res => setCategories(res.data || [])).catch(() => {});
    courseAPI.list(workspaceId).then(res => setCourses(res.data || [])).catch(() => {});
  }, [workspaceId]);

  const loadMeetings = async () => {
    try {
      const response = await meetingAPI.list(workspaceId);
      setMeetings(response.data);
    } catch (error) {
      console.error("Failed to load meetings:", error);
      toast.error("Erreur lors du chargement des réunions");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const [hours, minutes] = formData.scheduled_time.split(':');
      const scheduledAt = new Date(formData.scheduled_at);
      scheduledAt.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const data = {
        title: formData.title,
        description: formData.description,
        room_name: formData.room_name || null,
        scheduled_at: scheduledAt.toISOString(),
        duration_minutes: formData.duration_minutes,
        meeting_type: formData.meeting_type,
        external_link: formData.meeting_type !== "jitsi" ? formData.external_link : null,
        is_always_active: formData.is_always_active,
        category_id: formData.category_id || null,
      };

      const response = await meetingAPI.create(workspaceId, data);
      setMeetings([...meetings, response.data]);
      setDialogOpen(false);
      setFormData({
        title: "",
        description: "",
        room_name: "",
        scheduled_at: new Date(),
        scheduled_time: "10:00",
        duration_minutes: 60,
        meeting_type: "jitsi",
        external_link: "",
        is_always_active: false,
        category_id: "",
      });
      toast.success("Réunion créée avec succès");
    } catch (error) {
      console.error("Failed to create meeting:", error);
      toast.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);

    try {
      await meetingAPI.delete(workspaceId, deleteId);
      setMeetings(meetings.filter(m => m.id !== deleteId));
      toast.success("Réunion supprimée");
    } catch (error) {
      console.error("Failed to delete meeting:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const openEditDialog = (meeting) => {
    const scheduledDate = new Date(meeting.scheduled_at);
    const hours = scheduledDate.getHours().toString().padStart(2, '0');
    const minutes = scheduledDate.getMinutes().toString().padStart(2, '0');
    
    setEditingMeeting(meeting);
    setEditFormData({
      title: meeting.title,
      description: meeting.description || "",
      room_name: meeting.room_name || "",
      scheduled_at: scheduledDate,
      scheduled_time: `${hours}:${minutes}`,
      duration_minutes: meeting.duration_minutes,
      meeting_type: meeting.meeting_type,
      external_link: meeting.external_link || "",
      is_always_active: meeting.is_always_active || false,
      category_id: meeting.category_id || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingMeeting) return;
    setUpdating(true);

    try {
      const [hours, minutes] = editFormData.scheduled_time.split(':');
      const scheduledAt = new Date(editFormData.scheduled_at);
      scheduledAt.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const data = {
        title: editFormData.title,
        description: editFormData.description,
        room_name: editFormData.room_name || null,
        scheduled_at: scheduledAt.toISOString(),
        duration_minutes: editFormData.duration_minutes,
        meeting_type: editFormData.meeting_type,
        external_link: editFormData.meeting_type !== "jitsi" ? editFormData.external_link : null,
        is_always_active: editFormData.is_always_active,
        category_id: editFormData.category_id || null,
      };

      const response = await meetingAPI.update(workspaceId, editingMeeting.id, data);
      setMeetings(meetings.map(m => m.id === editingMeeting.id ? response.data : m));
      setEditDialogOpen(false);
      setEditingMeeting(null);
      toast.success("Réunion modifiée avec succès");
    } catch (error) {
      console.error("Failed to update meeting:", error);
      toast.error("Erreur lors de la modification");
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleActive = async (meeting) => {
    setTogglingId(meeting.id);
    try {
      const response = await meetingAPI.toggleActive(workspaceId, meeting.id);
      setMeetings(meetings.map(m => 
        m.id === meeting.id ? { ...m, is_always_active: response.data.is_always_active } : m
      ));
      toast.success(response.data.is_always_active ? "Réunion activée en permanence" : "Réunion désactivée");
    } catch (error) {
      console.error("Failed to toggle meeting:", error);
      toast.error("Erreur lors du changement de statut");
    } finally {
      setTogglingId(null);
    }
  };

  // Les réunions toujours actives apparaissent dans "À venir" même si la date est passée
  const upcomingMeetings = meetings.filter(m => isDateFuture(m.scheduled_at) || m.is_always_active).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const pastMeetings = meetings.filter(m => !isDateFuture(m.scheduled_at) && !m.is_always_active).sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));

  const getMeetingTypeLabel = (type) => {
    const types = {
      jitsi: "Jitsi",
      zoom: "Zoom",
      google_meet: "Google Meet",
      teams: "Microsoft Teams",
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Réunions</h1>
            <p className="mt-1 text-muted-foreground">
              {meetings.length} réunion(s) dans cet espace
            </p>
          </div>
          {canManage && (
            <div className="flex gap-2">
            <CategoryManagerButton />
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="create-meeting-btn">
                  <Plus className="h-4 w-4" />
                  Nouvelle réunion
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Planifier une réunion</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Titre *</Label>
                    <Input
                      id="title"
                      placeholder="Ex: Session de questions-réponses"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      data-testid="meeting-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Décrivez l'objectif de cette réunion..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      data-testid="meeting-description-input"
                    />
                  </div>
                  {formData.meeting_type === "jitsi" && (
                    <div className="space-y-2">
                      <Label htmlFor="room_name">Nom de la salle Jitsi</Label>
                      <Input
                        id="room_name"
                        placeholder="Ex: FormationDevOps (optionnel)"
                        value={formData.room_name}
                        onChange={(e) => setFormData({ ...formData, room_name: e.target.value.replace(/\s+/g, '') })}
                        data-testid="meeting-room-name-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Ce nom sera utilisé dans l'URL Jitsi. Laissez vide pour un nom auto-généré.
                      </p>
                    </div>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn("w-full justify-start text-left font-normal")}
                            data-testid="meeting-date"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(formData.scheduled_at, "PPP", { locale: fr })}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.scheduled_at}
                            onSelect={(date) => date && setFormData({ ...formData, scheduled_at: date })}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Heure</Label>
                      <Input
                        type="time"
                        value={formData.scheduled_time}
                        onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                        data-testid="meeting-time-input"
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Durée (minutes)</Label>
                      <Select 
                        value={formData.duration_minutes.toString()} 
                        onValueChange={(v) => setFormData({ ...formData, duration_minutes: parseInt(v) })}
                      >
                        <SelectTrigger data-testid="meeting-duration-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 heure</SelectItem>
                          <SelectItem value="90">1h30</SelectItem>
                          <SelectItem value="120">2 heures</SelectItem>
                          <SelectItem value="180">3 heures</SelectItem>
                          <SelectItem value="240">4 heures</SelectItem>
                          <SelectItem value="300">5 heures</SelectItem>
                          <SelectItem value="360">6 heures</SelectItem>
                          <SelectItem value="420">7 heures</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Plateforme</Label>
                      <Select 
                        value={formData.meeting_type} 
                        onValueChange={(v) => setFormData({ ...formData, meeting_type: v })}
                      >
                        <SelectTrigger data-testid="meeting-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="jitsi">Jitsi (intégré)</SelectItem>
                          <SelectItem value="zoom">Zoom</SelectItem>
                          <SelectItem value="google_meet">Google Meet</SelectItem>
                          <SelectItem value="teams">Microsoft Teams</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {formData.meeting_type !== "jitsi" && (
                    <div className="space-y-2">
                      <Label>Lien de la réunion</Label>
                      <Input
                        placeholder="https://..."
                        value={formData.external_link}
                        onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                        data-testid="meeting-link-input"
                      />
                    </div>
                  )}
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="is_always_active" className="text-base cursor-pointer">
                        Toujours actif
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        La réunion restera accessible même après la date prévue
                      </p>
                    </div>
                    <Switch
                      id="is_always_active"
                      checked={formData.is_always_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_always_active: checked })}
                      data-testid="meeting-always-active-switch"
                    />
                  </div>
                  {categories.length > 0 && (
                    <div className="space-y-2">
                      <Label>Catégorie</Label>
                      <Select 
                        value={formData.category_id || "none"} 
                        onValueChange={(v) => setFormData({ ...formData, category_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger data-testid="meeting-category-select">
                          <SelectValue placeholder="Aucune catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucune catégorie</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded" style={{ backgroundColor: cat.color }} />
                                {cat.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" disabled={creating} data-testid="meeting-submit-btn">
                      {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Créer
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        {/* Meetings List */}
        {meetings.length === 0 ? (
          <Card className="mt-8 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Video className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Aucune réunion</h3>
              <p className="mt-2 text-sm text-muted-foreground text-center">
                {canManage 
                  ? "Planifiez votre première réunion avec vos apprenants"
                  : "Aucune réunion planifiée pour le moment"
                }
              </p>
              {canManage && (
                <Button className="mt-4 gap-2" onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Planifier une réunion
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="mt-8 space-y-8">
            {/* Upcoming */}
            {upcomingMeetings.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  À venir
                  <Badge variant="secondary" className="ml-2">{upcomingMeetings.length}</Badge>
                </h2>
                <div className="grid gap-4">
                  {upcomingMeetings.map((meeting, index) => {
                    const daysUntil = getDaysUntil(meeting.scheduled_at);
                    const isPast = !isDateFuture(meeting.scheduled_at);
                    return (
                      <Card 
                        key={meeting.id} 
                        className={cn("card-hover", meeting.is_always_active && "border-green-500/50")}
                        data-testid={`meeting-card-${index}`}
                      >
                        <CardContent className="flex items-center gap-4 p-4">
                          <div className={cn(
                            "flex h-12 w-12 items-center justify-center rounded-lg shrink-0",
                            meeting.is_always_active ? "bg-green-500/10" : "bg-primary/10"
                          )}>
                            {meeting.is_always_active ? (
                              <Infinity className="h-6 w-6 text-green-500" />
                            ) : (
                              <Video className="h-6 w-6 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold">{meeting.title}</p>
                              <Badge variant="secondary">{getMeetingTypeLabel(meeting.meeting_type)}</Badge>
                              {meeting.is_always_active && (
                                <Badge className="bg-green-500 text-white">
                                  <Infinity className="h-3 w-3 mr-1" />
                                  Toujours actif
                                </Badge>
                              )}
                              {!meeting.is_always_active && daysUntil === 0 && (
                                <Badge className="bg-green-500 text-white meeting-live">Aujourd'hui</Badge>
                              )}
                              {isPast && meeting.is_always_active && (
                                <Badge variant="outline" className="text-orange-500 border-orange-500">Date passée</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                              {meeting.description || "Pas de description"}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3" />
                                {formatDateTime(meeting.scheduled_at)}
                              </span>
                              <span>{meeting.duration_minutes} min</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {(meeting.meeting_url && (meeting.is_always_active || !isPast)) && (
                              <Link to={`/workspace/${workspaceId}/meetings/${meeting.id}`}>
                                <Button size="sm" className="gap-1">
                                  <Video className="h-4 w-4" />
                                  Rejoindre
                                </Button>
                              </Link>
                            )}
                            {canManage && (meeting.is_always_active || daysUntil === 0) && (
                              <Link to={`/workspace/${workspaceId}/meetings/${meeting.id}/monitor`}>
                                <Button size="sm" variant="outline" className="gap-1">
                                  <Monitor className="h-4 w-4" />
                                  Surveillance
                                </Button>
                              </Link>
                            )}
                            {canManage && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleActive(meeting)}
                                  disabled={togglingId === meeting.id}
                                  title={meeting.is_always_active ? "Désactiver" : "Garder toujours actif"}
                                  data-testid={`toggle-meeting-${index}`}
                                >
                                  {togglingId === meeting.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : meeting.is_always_active ? (
                                    <Power className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <PowerOff className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(meeting)}
                                  data-testid={`edit-meeting-${index}`}
                                >
                                  <Pencil className="h-4 w-4 text-primary" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteId(meeting.id)}
                                  data-testid={`delete-meeting-${index}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </>
                            )}
                          </div>
                        </CardContent>
                        {/* Live Presence Dashboard for active meetings */}
                        {canManage && (meeting.is_always_active || daysUntil === 0) && (
                          <div style={{ padding: '0 16px 16px' }}>
                            <LiveAttendanceDashboard sessionId={`meeting:${meeting.id}`} workspaceId={workspaceId} />
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Past */}
            {pastMeetings.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-muted-foreground">
                  Passées
                  <Badge variant="outline" className="ml-2">{pastMeetings.length}</Badge>
                </h2>
                <div className="grid gap-4">
                  {pastMeetings.map((meeting, index) => (
                    <Card key={meeting.id} className="opacity-75 hover:opacity-100 transition-opacity">
                      <CardContent className="flex items-center gap-4 p-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted shrink-0">
                          <Video className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{meeting.title}</p>
                            <Badge variant="outline">{getMeetingTypeLabel(meeting.meeting_type)}</Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            <span>{formatDateTime(meeting.scheduled_at)}</span>
                            <span>{meeting.duration_minutes} min</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {canManage && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleToggleActive(meeting)}
                                disabled={togglingId === meeting.id}
                                title="Réactiver cette réunion"
                                data-testid={`toggle-past-meeting-${index}`}
                              >
                                {togglingId === meeting.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Power className="h-4 w-4 text-muted-foreground hover:text-green-500" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteId(meeting.id)}
                                data-testid={`delete-past-meeting-${index}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </CardContent>
                      {/* Presence history for past meetings */}
                      {canManage && (
                        <div style={{ padding: '0 16px 16px' }}>
                          <LiveAttendanceDashboard sessionId={`meeting:${meeting.id}`} workspaceId={workspaceId} />
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette réunion ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Meeting Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Modifier la réunion</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Titre *</Label>
                <Input
                  id="edit-title"
                  placeholder="Ex: Session de questions-réponses"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  required
                  data-testid="edit-meeting-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  placeholder="Décrivez l'objectif de cette réunion..."
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={2}
                  data-testid="edit-meeting-description-input"
                />
              </div>
              {editFormData.meeting_type === "jitsi" && (
                <div className="space-y-2">
                  <Label htmlFor="edit-room_name">Nom de la salle Jitsi</Label>
                  <Input
                    id="edit-room_name"
                    placeholder="Ex: FormationDevOps (optionnel)"
                    value={editFormData.room_name}
                    onChange={(e) => setEditFormData({ ...editFormData, room_name: e.target.value.replace(/\s+/g, '') })}
                    data-testid="edit-meeting-room-name-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ce nom sera utilisé dans l'URL Jitsi. Laissez vide pour un nom auto-généré.
                  </p>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full justify-start text-left font-normal")}
                        data-testid="edit-meeting-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(editFormData.scheduled_at, "PPP", { locale: fr })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={editFormData.scheduled_at}
                        onSelect={(date) => date && setEditFormData({ ...editFormData, scheduled_at: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Heure</Label>
                  <Input
                    type="time"
                    value={editFormData.scheduled_time}
                    onChange={(e) => setEditFormData({ ...editFormData, scheduled_time: e.target.value })}
                    data-testid="edit-meeting-time-input"
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Durée (minutes)</Label>
                  <Select 
                    value={editFormData.duration_minutes.toString()} 
                    onValueChange={(v) => setEditFormData({ ...editFormData, duration_minutes: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="edit-meeting-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 heure</SelectItem>
                      <SelectItem value="90">1h30</SelectItem>
                      <SelectItem value="120">2 heures</SelectItem>
                      <SelectItem value="180">3 heures</SelectItem>
                      <SelectItem value="240">4 heures</SelectItem>
                      <SelectItem value="300">5 heures</SelectItem>
                      <SelectItem value="360">6 heures</SelectItem>
                      <SelectItem value="420">7 heures</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Plateforme</Label>
                  <Select 
                    value={editFormData.meeting_type} 
                    onValueChange={(v) => setEditFormData({ ...editFormData, meeting_type: v })}
                  >
                    <SelectTrigger data-testid="edit-meeting-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="jitsi">Jitsi (intégré)</SelectItem>
                      <SelectItem value="zoom">Zoom</SelectItem>
                      <SelectItem value="google_meet">Google Meet</SelectItem>
                      <SelectItem value="teams">Microsoft Teams</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editFormData.meeting_type !== "jitsi" && (
                <div className="space-y-2">
                  <Label>Lien de la réunion</Label>
                  <Input
                    placeholder="https://..."
                    value={editFormData.external_link}
                    onChange={(e) => setEditFormData({ ...editFormData, external_link: e.target.value })}
                    data-testid="edit-meeting-link-input"
                  />
                </div>
              )}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="edit_is_always_active" className="text-base cursor-pointer">
                    Toujours actif
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    La réunion restera accessible même après la date prévue
                  </p>
                </div>
                <Switch
                  id="edit_is_always_active"
                  checked={editFormData.is_always_active}
                  onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_always_active: checked })}
                  data-testid="edit-meeting-always-active-switch"
                />
              </div>
              {categories.length > 0 && (
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select 
                    value={editFormData.category_id || "none"} 
                    onValueChange={(v) => setEditFormData({ ...editFormData, category_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="edit-meeting-category-select">
                      <SelectValue placeholder="Aucune catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune catégorie</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded" style={{ backgroundColor: cat.color }} />
                            {cat.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={updating} data-testid="edit-meeting-submit-btn">
                  {updating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Enregistrer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </WorkspaceLayout>
  );
}
